import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { PMField } from "../../../utils/PMField";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { mapErrors } from "../../../Utils";
import PMSwitch from "../../../utils/PMSwitch";
import {
  createCountAsync,
  updateCountAsync,
  getCountsAsync,
  getCountAsync,
} from "../../../../slices/inventory/count";
import { getStorages } from "../../../../slices/inventory/storage";
import ItemSelectorV2 from "../../../utils/Selector/ItemSelectorV2";
import { FormControl, InputLabel, Select, MenuItem, Button } from "@mui/material";
import { BuildingOffice2Icon, DocumentDuplicateIcon } from "@heroicons/react/24/outline";
import { fetchCountItems } from "../../../../api/inventory/count_items";

export default function InventoryCountModal(props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { open, onClose, itemId } = props;
  const storeState = useSelector((store) => {
    return store.inventory_counts;
  });
  const [itemState, setItemState] = useState(storeState);
  const [productSelectorOpen, setProductSelectorOpen] = useState(false);
  const [copyFromCountOpen, setCopyFromCountOpen] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedStorageId, setSelectedStorageId] = useState(null);

  const { count, isLoading } = itemState;
  const storagesState = useSelector((store) => store.inventory_storages);
  const storages = storagesState?.data || [];
  const countsState = useSelector((store) => store.inventory_counts);
  const counts = countsState?.data || [];

  useEffect(() => {
    if (open && storages.length === 0) {
      dispatch(getStorages());
    }
    if (open && counts.length === 0 && !itemId) {
      dispatch(getCountsAsync());
    }
    if (itemId && count?.id) {
      // When editing, set the storage_id from the count
      if (count.storage_id) {
        setSelectedStorageId(count.storage_id);
      }
      setItemState(storeState);
    } else if (!itemId) {
      // When creating new, reset everything
      setItemState({ ...itemState, count: null });
      setSelectedProducts([]);
      setSelectedStorageId(null);
    }
  }, [open, itemId, count?.id, count?.storage_id]);

  const createOrUpdateDispatch = (data) => {
    const payload = {
      ...data,
      storage_id: selectedStorageId,
      product_ids: selectedProducts.map(p => p.id),
    };
    
    if (count?.id) {
      const res = dispatch(updateCountAsync(payload));
      return res;
    } else {
      const res = dispatch(createCountAsync(payload));
      return res;
    }
  };

  const handleLoadWarehouseProducts = async () => {
    if (!selectedStorageId) {
      toast.error("Velg et lager først");
      return;
    }
    // Open product selector with filter for warehouse-enabled products for selected storage
    setProductSelectorOpen(true);
  };

  const handleCopyFromCount = async (sourceCountId) => {
    try {
      const response = await fetchCountItems(sourceCountId);
      if (response && response.data && Array.isArray(response.data)) {
        // Extract unique products from count items
        const products = response.data
          .map(item => item.product)
          .filter(product => product && product.id)
          .filter((product, index, self) => 
            index === self.findIndex(p => p.id === product.id)
          );
        
        if (products.length > 0) {
          setSelectedProducts(products);
          toast.success(`Kopierte ${products.length} produkter fra varetelling`);
        } else {
          toast.error("Ingen produkter funnet i valgt varetelling");
        }
      } else {
        toast.error("Kunne ikke hente produkter fra varetelling");
      }
      setCopyFromCountOpen(false);
    } catch (error) {
      console.error("Error copying from count:", error);
      toast.error("Kunne ikke kopiere produkter");
      setCopyFromCountOpen(false);
    }
  };

  const handleProductSelect = (products) => {
    setSelectedProducts(products || []);
    setProductSelectorOpen(false);
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as="div"
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as="div"
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="w-full sm:max-w-2xl max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="mt-3 sm:mt-5">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    {itemId ? t("storage.edit") : t("storage.add")}
                  </Dialog.Title>

                  {isLoading ? (
                    "Loading..."
                  ) : (
                    <Formik
                      initialValues={{
                        id: count?.id,
                        count_number: count?.count_number,
                        title: count?.title,
                        description: count?.description,
                        active: count?.active || true,
                        storage_id: count?.storage_id || null,
                        submit: null,
                      }}
                      validationSchema={Yup.object({
                        count_number: Yup.string().max(50),
                        title: Yup.string()
                          .max(255)
                          .required(t("title.required")),
                        description: Yup.string().max(500),
                      })}
                      onSubmit={(values, helpers) => {
                        if (!itemId && !selectedStorageId) {
                          toast.error("Velg et lager");
                          helpers.setSubmitting(false);
                          return;
                        }
                        toast("Saving");
                        try {
                          toast.promise(createOrUpdateDispatch(values), {
                            loading: t("Saving...."),
                            success: (data) => {
                              if (data?.status === 500)
                                throw new Error("Server error");
                              if (data?.payload?.errors)
                                throw new Error(
                                  mapErrors(data?.payload?.errors)
                                );
                              onClose(true);
                              return t("Data was saved!");
                            },
                            error: (error) => {
                              return t(
                                "Something went wrong. Could not save data!"
                              );
                            },
                          });
                          helpers.setStatus({ success: true });
                          helpers.setSubmitting(false);
                        } catch (err) {
                          toast.error("Something went wrong...");
                          console.error(err);
                          helpers.setStatus({ success: false });
                          helpers.setErrors({ submit: err.message });
                          helpers.setSubmitting(false);
                        }
                      }}
                    >
                      {(formik) => (
                        <Form>
                          <div className="grid grid-cols-6 gap-6 mt-10">
                            <div className="col-span-6 sm:col-span-3">
                              <PMField
                                name="count_number"
                                type="text"
                                label="Count Number"
                                disabled
                                {...formik}
                              />
                            </div>

                            <div className="col-span-6 sm:col-span-3">
                              <PMField
                                name="title"
                                type="text"
                                label="Title"
                                {...formik}
                              />
                            </div>

                            <div className="col-span-6 sm:col-span-6">
                              <PMField
                                name="description"
                                type="text"
                                label="Description"
                                multiline
                                rows={2}
                                {...formik}
                              />
                            </div>

                            <div className="col-span-6 sm:col-span-3">
                              <PMField
                                name="date"
                                type="date"
                                label="Date"
                                {...formik}
                              />
                            </div>

                            <div className="col-span-6 sm:col-span-3">
                              <FormControl fullWidth variant="standard">
                                <InputLabel>Lager</InputLabel>
                                <Select
                                  value={selectedStorageId || formik.values.storage_id || ""}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setSelectedStorageId(value || null);
                                    formik.setFieldValue("storage_id", value || null);
                                  }}
                                  label="Lager"
                                >
                                  <MenuItem value="">
                                    <em>Ingen valgt</em>
                                  </MenuItem>
                                  {storages.map((storage) => (
                                    <MenuItem key={storage.id} value={storage.id}>
                                      {storage.title || storage.storage_number || `Lager ${storage.id}`}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </div>

                            {!itemId && (
                              <>
                                <div className="col-span-6 sm:col-span-6">
                                  <div className="border-t border-gray-200 pt-4">
                                    <h4 className="text-sm font-medium text-gray-900 mb-3">
                                      Produkter
                                    </h4>
                                    <div className="space-y-2">
                                      <Button
                                        variant="outlined"
                                        startIcon={<BuildingOffice2Icon className="h-5 w-5" />}
                                        onClick={() => setProductSelectorOpen(true)}
                                        fullWidth
                                      >
                                        Velg lagerførte produkter
                                      </Button>
                                      
                                      {selectedStorageId && (
                                        <Button
                                          variant="outlined"
                                          color="secondary"
                                          onClick={handleLoadWarehouseProducts}
                                          fullWidth
                                        >
                                          Hent produkter for valgt lager
                                        </Button>
                                      )}

                                      <Button
                                        variant="outlined"
                                        startIcon={<DocumentDuplicateIcon className="h-5 w-5" />}
                                        onClick={() => setCopyFromCountOpen(true)}
                                        fullWidth
                                      >
                                        Kopier fra annen varetelling
                                      </Button>

                                      {selectedProducts.length > 0 && (
                                        <div className="mt-3 p-3 bg-gray-50 rounded">
                                          <p className="text-sm font-medium text-gray-700 mb-2">
                                            Valgte produkter ({selectedProducts.length}):
                                          </p>
                                          <div className="space-y-1">
                                            {selectedProducts.map((product) => (
                                              <div
                                                key={product.id}
                                                className="text-sm text-gray-600"
                                              >
                                                • {product.displayTitle || product.title || product.item_number}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </>
                            )}

                            <div className="col-span-6 sm:col-span-6">
                              <PMSwitch
                                name="active"
                                label="Active"
                                {...formik}
                              />
                            </div>

                            <div className="col-span-6 sm:col-span-3 lg:col-span-2"></div>
                          </div>

                          <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                            <button
                              type="button"
                              className="inline-flex justify-center w-20 rounded-md border border-transparent bg-gray-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                              onClick={onClose}
                            >
                              {t("Close")}{" "}
                            </button>

                            <button
                              type="button"
                              className="inline-flex align-right justify-center rounded-md border border-transparent bg-emerald-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                              onClick={formik.submitForm}
                            >
                              {count?.id ? t("Save") : t("Create")}{" "}
                            </button>
                          </div>
                        </Form>
                      )}
                    </Formik>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>

      {/* Product Selector */}
      <ItemSelectorV2
        open={productSelectorOpen}
        onChange={handleProductSelect}
        module="product"
        selectedItems={selectedProducts}
        autoClose={true}
        compact={true}
        multiple={true}
        filters={[
          { key: "warehouse_enabled", value: true },
          ...(selectedStorageId ? [{ key: "storage_id", value: selectedStorageId }] : []),
        ]}
        onClose={() => setProductSelectorOpen(false)}
      />

      {/* Copy from Count Dialog */}
      <Transition.Root show={copyFromCountOpen} as={Fragment}>
        <Dialog as="div" className="relative z-20" onClose={() => setCopyFromCountOpen(false)}>
          <Transition.Child
            as="div"
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as="div"
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="w-full sm:max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 mb-4"
                  >
                    Kopier fra varetelling
                  </Dialog.Title>
                  
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {counts.length === 0 ? (
                      <p className="text-sm text-gray-500">Ingen varetellinger tilgjengelig</p>
                    ) : (
                      counts.map((countItem) => (
                        <button
                          key={countItem.id}
                          onClick={() => handleCopyFromCount(countItem.id)}
                          className="w-full text-left p-3 border border-gray-200 rounded hover:bg-gray-50 transition-colors"
                        >
                          <div className="font-medium text-gray-900">
                            {countItem.title || countItem.count_number || `Varetelling #${countItem.id}`}
                          </div>
                          {countItem.count_number && (
                            <div className="text-sm text-gray-500">
                              #{countItem.count_number}
                            </div>
                          )}
                        </button>
                      ))
                    )}
                  </div>

                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                      onClick={() => setCopyFromCountOpen(false)}
                    >
                      Avbryt
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </Transition.Root>
  );
}
